'use client';

import { Box } from '@mui/material';
import { usePathname, redirect } from 'next/navigation';
import { type PropsWithChildren } from 'react';

import {
  backupLandingPath,
  Console,
  consoleForPathname,
  consoleLandingPath,
} from '@/components/layout/vertical/console/console';
import { Loader } from '@/components/shared/loader';
import { useFeatureFlags, useLaunchDarkly } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useUser } from '@/contexts/useUser';
import { getDocsUrl } from '@/docs/docsUrls';

export function AuthChecker({ children }: PropsWithChildren) {
  const { user, currentProjectId } = useUser();
  const {
    SkipOnboarding,
    SaasInventoryDefaultLanding,
    DataEngineDefaultLanding,
  } = useFeatureFlags();
  const pathname = usePathname();
  const { isReady } = useLaunchDarkly();
  const { sourceAccounts } = useSourceAccounts();
  const { isGlobalRole, roles, permissions } = useRoles();

  const isApplicationReady =
    !!user &&
    !!currentProjectId &&
    !!sourceAccounts &&
    isReady &&
    roles.length > 0 &&
    Object.keys(permissions).length > 0;

  if (!isApplicationReady) {
    return <LoaderComponent />;
  }

  if (
    user.eonAccount?.domain === 'eon-docs' &&
    !isGlobalRole(user.selectedRole)
  ) {
    redirect(getDocsUrl('root'));
  }

  // Before the source-account check: a Data Platform account may have no backup source accounts to onboard.
  if (DataEngineDefaultLanding && pathname === '/') {
    redirect(
      consoleLandingPath(Console.DataPlatform, SaasInventoryDefaultLanding)
    );
  }

  if (
    sourceAccounts?.length ||
    SkipOnboarding ||
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/global-management') ||
    (DataEngineDefaultLanding &&
      consoleForPathname(pathname) === Console.DataPlatform)
  ) {
    // Redirect above VerticalLayout so the nav shell never flashes before the
    // flag-gated landing resolves.
    if (pathname === '/') {
      redirect(backupLandingPath(SaasInventoryDefaultLanding));
    }
    return children;
  }

  // redirect('/global-management/scanning-accounts');
  redirect('/onboarding');
}

const LoaderComponent = () => (
  <Box
    className='h-full'
    sx={{
      background: 'var(--mui-palette-background-pageGradient)',
    }}
  >
    <Loader />
  </Box>
);
